// Global
import { Image as JSSImage, ImageField } from '@sitecore-content-sdk/nextjs';
import { ImageProps } from 'next/image';

// Lib
import { parseUrlObject } from 'lib/utils/string-utils';
import useIsEditing from 'lib/hooks/sitecore/useIsEditing';
import { useFieldWithFallbacks } from 'lib/hooks/sitecore/useFieldWithFallbacks';
import EnhancedNextImage from 'components/helpers/Atoms/EnhancedNextImage/EnhancedNextImage';

/**
 * JSS does not yet support Next Image in Experience Editor
 * This component will switch between the two based on environment
 * which allows us to get the various performance benefits from Next Image.
 *
 * NOTE: Images may display slightly differently in
 * Experience Editor as the JSS Image component doesn't have the same layout options.
 */

interface SizedImageFieldProps extends ImageField {
  value?: {
    alt?: string;
    height: number | `${number}`;
    src?: string;
    width: number | `${number}`;
  };
}

export interface ImageWrapperProps {
  className?: string;
  editable?: boolean;
  fill?: boolean;
  field?: SizedImageFieldProps | ImageField;
  fallbacks?: (SizedImageFieldProps | ImageField | undefined)[];
  fallbackHeight?: number;
  fallbackWidth?: number;
  priority?: boolean;
  sizes?: string;
}

/**
 * This hook is used to render an image field.
 * This is needed because a component cannot return null.
 * This needs to be a hook because we need to use the useIsEditing hook to determine if we are in edit mode.
 * @param props - The props for the image wrapper.
 * @returns A JSX.Element if the field has a value, or if we are editing, otherwise null.
 */
export function useImageWrapper(props: ImageWrapperProps): JSX.Element | null {
  const { field, fallbacks, editable = true } = props;
  const isEditing = useIsEditing() && editable;

  const { renderField } = useFieldWithFallbacks(field, fallbacks);

  const hasValue = !!renderField?.value?.src;

  // We should only render if it has a value, or if we are editing
  const shouldRender = hasValue || isEditing;
  if (!shouldRender) {
    return null;
  }
  return <ImageWrapper {...props} />;
}

const ImageWrapper = ({
  className,
  editable,
  fill,
  field,
  fallbacks,
  fallbackHeight,
  fallbackWidth,
  priority,
  // Default to 100vw for sizes if not provided to ensure responsive images
  sizes = '100vw',
}: ImageWrapperProps): JSX.Element => {
  const isEditing = useIsEditing();

  const { renderField } = useFieldWithFallbacks(field, fallbacks);

  // If there's no field, don't render anything. This should only be the case
  // if the field itself is undefined, which should only happen if code is deployed
  // before data templates are published, so this is just a safety check
  if (!renderField) {
    return <></>;
  }

  const { alt, height, src, width } = renderField?.value || {};
  const imageHeight = height ?? fallbackHeight;
  const imageWidth = width ?? fallbackWidth;

  if (isEditing) {
    return (
      <JSSImage
        data-component="helpers/fieldwrappers/imagewrapper"
        editable={editable}
        field={renderField}
        className={className}
      />
    );
  }

  const newSrc = normalizeSitecoreImageUrl(src);

  if (!newSrc) {
    // If there's no src, only render it if we're in editing mode.

    return <></>;
  }

  const nextImageProps: ImageProps = {
    alt: (alt as string) || '',
    height: fill ? undefined : (imageHeight as number),
    width: fill ? undefined : (imageWidth as number),
    className: className,
    fill,
    priority,
    sizes,
    src: newSrc,
  };

  // for images that are missing width property.
  if (!nextImageProps.fill && (!nextImageProps.width || !nextImageProps.height)) {
    // Copy the field instead of updating the original
    const newField = structuredClone(renderField);

    newField.value = {
      ...newField.value,
      src: newSrc,
    };
    return (
      <JSSImage
        {...nextImageProps}
        data-component="helpers/fieldwrappers/imagewrapper"
        field={newField}
      />
    );
  }

  return (
    <EnhancedNextImage data-component="helpers/fieldwrappers/imagewrapper" {...nextImageProps} />
  );
};

export default ImageWrapper;

/**
 * To support preview site we normalize media urls to strip out the domain if it is coming from Sitecore.
 */

export function normalizeSitecoreImageUrl(src: string | undefined) {
  let newSrc = src;

  if (src) {
    const imageUrl = parseUrlObject(src);

    // If we're using the preview endpoint, replace the domain
    if (imageUrl?.pathname.startsWith('/-/media/')) {
      // `process.env.SITECORE_API_HOST` is only exposed for localhost.
      // In localhost XMC throws an error proxying from http to https, so we
      // need to use the SITECORE_API_HOST directly.
      // Otherwise we can just remove the domain and it will resolve correctly,
      // either on CM directly, or it will be proxied to the CM via the rewrite rule
      newSrc = imageUrl.origin
        ? src.replace(imageUrl.origin, process.env.SITECORE_API_HOST || '')
        : src;
    }
  }

  return newSrc;
}
